import { AxiosResponse } from "axios";
import jwt from "jsonwebtoken";
import { TokenApi } from "../api";
import { JwtTokenData, User } from "../types";
import isPast from "date-fns/isPast";
import differenceInSeconds from "date-fns/differenceInSeconds";

const refreshTokenKey = "jwt-refresh";
const jwtTokenKey = "jwt-auth";
const communityKey = "cache-community";
const userKey = "ct-user";

// A class that can handle all of the token related business logic.
class TokenHelper {
  tokenApi: TokenApi;

  constructor(tokenApi: TokenApi) {
    this.tokenApi = tokenApi;
  }

  getJwtToken = () => {
    if (!process?.browser) {
      // Running server side.
      return "";
    }

    const token = localStorage.getItem(jwtTokenKey) || "";
    return token;
  };

  decodeJWT = (jwtToken: string) => {
    return jwt.decode(jwtToken) as JwtTokenData | undefined;
  };

  removeJwtToken = () => {
    if (!process?.browser) {
      // Running server side.
      return;
    }

    localStorage.removeItem(jwtTokenKey);
  };

  setRefreshToken = (refreshToken: string) => {
    localStorage.setItem(refreshTokenKey, refreshToken);
  };

  formatAuthHeader = (token: string) => {
    return `Token token=${token}`;
  };

  getCommunitySlug = () => {
    if (!process?.browser) {
      // Running server side.
      return "";
    }
    const cachedUser = JSON.parse(localStorage[userKey] || "{}") as
      | User
      | undefined;

    if (cachedUser?.organization?.community?.slug) {
      return cachedUser.organization.community.slug;
    }

    const cachedCommunity = JSON.parse(localStorage[communityKey] || "{}") as
      | {
          slug: string;
        }
      | undefined;

    if (cachedCommunity?.slug) {
      return cachedCommunity.slug;
    }

    return "";
  };

  getCommunityId = () => {
    if (!process?.browser) {
      // Running server side.
      return -1;
    }
    const cachedUser = JSON.parse(localStorage[userKey] || "{}") as
      | User
      | undefined;

    if (cachedUser?.organization?.community?.id) {
      return cachedUser.organization.community.id;
    }

    const cachedCommunity = JSON.parse(localStorage[communityKey] || "{}") as
      | {
          id: number;
        }
      | undefined;

    if (cachedCommunity?.id) {
      return cachedCommunity.id;
    }

    return -1;
  };

  refreshJwt = (token: string) => {
    const communityId = this.getCommunityId();

    return this.tokenApi.refreshJwt(token, communityId).then((res: any) => {
      if (!res.data?.token?.auth) {
        this.removeJwtToken();
        throw new Error("Unable to refresh current jwt");
        return;
      }

      localStorage.setItem(jwtTokenKey, res.data.token.auth);
    }) as Promise<AxiosResponse<User>>;
  };

  jwtIsExpired = () => {
    const token = localStorage.getItem(jwtTokenKey) || "";

    if (!token) {
      return true;
    }
    const data = jwt.decode(token) as JwtTokenData;
    const exp = new Date(data.exp * 1000);

    if (isPast(exp)) {
      return true;
    }

    return false;
  };

  withinExchangeWindow = () => {
    const token = localStorage.getItem(jwtTokenKey) || "";

    if (!token) return false;
    const tokenData = jwt.decode(token) as JwtTokenData;
    const now = new Date();
    const expDate = new Date(tokenData.exp * 1000);
    return differenceInSeconds(expDate, now) <= 30;
  };

  // 🤮. Not nice that we have two token systems.
  // Makes for a insecure app and messy token management on the FE.
  // Continue the pattern until it can be reworked.
  getRefreshToken = () => {
    if (!process?.browser) {
      // Running server side.
      return "";
    }

    const token = localStorage.getItem(refreshTokenKey) || "";
    return token;
  };
}

export default TokenHelper;
